import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useDebounceFn } from 'ahooks';
import { Check, ChevronDown, Search } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { Input as RoccoInput } from '@rocco/components/input';
import { Label as RoccoLabel } from '@rocco/components/label';
import { PillButton } from '@rocco/components/pill-button';
import { getSafeSwatchBackground } from '@rocco/ui/filter-sort/utils/color-swatch';
import type { IUseCurrentLocationResult } from '~/modules/location/hook/use-current-location';
import type {
    AggregateRequest,
    AggregateState,
} from '@modules/search/hook/use-aggregation';
import type {
    FilterState,
    FilterStates,
    FilterStatesKey,
    SearchListKeys,
} from '@modules/search/type/filter-state';
import { SEARCH_LIST_AGG_PARAM_KEY_MAP } from '@modules/search/config/search-filter';
import type { SearchAggregationParams } from '@modules/search/type/api';
import { compareSearchListLabels } from '@modules/search/util/compare-search-list-labels';
import type { FilterType } from '@modules/search/components/filter-drawer/FilterFactory';
import type {
    AwardsFilter,
    AwardFilterState,
    AwardCheckboxTreeNode,
} from '@modules/search/components/awards-filter-tree/AwardsFilterTree';
import type {
    LocationFilter,
    LocationFilterState,
} from '@modules/search/components/filter-drawer/FilterLocation';
import type { BasicPlace } from '~/modules/location/type';
import {
    formatLocationSearchFilterText,
    MIN_PLACE_FINDER_KEYWORD_LENGTH,
    usePlaceFinder,
} from '~/modules/location/hook/use-place-finder';
import { FindPlaceApiProviderEnum, FindPlaceTypeEnum } from 'generated/graphql';
import { useCountryContext } from '@archipro-website/localisation';
import { getLocationFilterPlaceholder } from '@rocco/utils/format-location';
import {
    AwardsCheckboxTree,
    type AwardsTreeGroup,
} from '@ui-rocco/components/awards-checkbox-tree/AwardsCheckboxTree';
import { applyAwardsSelection } from '@modules-rocco/filters/filter-state/awards-filter-state';
import type {
    SearchListFilter,
    SearchListFilterState,
    FilterSearchListItem,
} from '@modules/search/components/filter-drawer/FilterSearchList';
import type {
    CheckboxGroupFilter,
    CheckboxGroupFilterState,
} from '@modules/search/components/filter-drawer/FilterCheckboxGroup';
import type {
    RadioGroupFilter,
    RadioGroupFilterState,
} from '@modules/search/components/filter-drawer/FilterRadioGroup';
import type {
    CheckboxFilter,
    CheckboxFilterState,
} from '@modules/search/components/filter-drawer/FilterCheckbox';
import type {
    RangeSelectorFilter,
    RangeSelectorFilterState,
} from '@modules/search/components/filter-drawer/FilterRangeSelector';

type UpdateFilter = (
    filterKey: FilterStatesKey,
    state: Partial<FilterState>,
    updateStates?: boolean,
    subFilterKey?: string,
) => void;

/** Shared context threaded from the sheet into every drill-in control. */
export interface RefineControlContext extends IUseCurrentLocationResult {
    filterStates: FilterStates;
    updateFilter: UpdateFilter;
    aggregate: (request: AggregateRequest) => void;
    /** Marketing/admin view — reveals adminOnly filters. */
    marketingView?: boolean;
}

const LOAD_SIZE = 40;

/**
 * One option row, matching the category Refine list: label on the left, a rocco
 * checkbox on the right. Checkbox and label are linked by id so the whole row is
 * tappable.
 */
const CheckboxRow = ({
    id,
    label,
    checked,
    disabled,
    onToggle,
}: {
    id: string;
    label: string;
    checked: boolean;
    disabled?: boolean;
    onToggle: (checked: boolean) => void;
}) => (
    <div className="flex flex-row-reverse items-center justify-between gap-3">
        <RoccoCheckbox
            id={id}
            checked={checked}
            disabled={disabled}
            onCheckedChange={(next) => onToggle(next === true)}
        />
        <RoccoLabel htmlFor={id} target="checkboxReversed" singleLine>
            {label}
        </RoccoLabel>
    </div>
);

/**
 * The searchable checkbox list (Brand / Colour / Designer …) rendered with the
 * category Refine's own primitives — a full-width rocco search input over a
 * checkbox list with a "Display more" — but driven by the modules
 * `updateFilter` + `aggregate` so the counts stay scoped to this professional.
 */
export const RefineSearchableList = ({
    filter,
    state,
    ctx,
}: {
    filter: SearchListFilter;
    state: SearchListFilterState;
    ctx: RefineControlContext;
}) => {
    const { filterKey, subFilterKey, props } = filter;
    const { items, search, hasMoreItems, after, selectedItems } = state;
    const { filterStates, updateFilter, aggregate, loading } = ctx;
    const [aggregating, setAggregating] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search ?? '');
    const stateRef = useRef(state);
    stateRef.current = state;
    const searchGenerationRef = useRef(0);
    const lastSearchQueryRef = useRef<string | null>(null);
    const requestInFlightRef = useRef(false);
    const searchFocusedRef = useRef(false);

    useEffect(() => {
        if (!searchFocusedRef.current) {
            setDraftSearch(search ?? '');
        }
    }, [search]);

    const applyAggregatedItems = (
        request: AggregateRequest,
        aggState: AggregateState,
        data?: FilterStates,
        generation = searchGenerationRef.current,
    ) => {
        if (generation !== searchGenerationRef.current) return;
        if (aggState === 'cancel') {
            requestInFlightRef.current = false;
            lastSearchQueryRef.current = null;
            setAggregating(false);
            return;
        }
        if (aggState !== 'loaded') return;
        requestInFlightRef.current = false;
        setAggregating(false);
        const incoming = data?.[filterKey];
        let listState: SearchListFilterState | null = null;
        if (Array.isArray(incoming)) {
            incoming.forEach((sub) => {
                if (typeof sub !== 'number' && sub.id === subFilterKey) {
                    listState = sub.state;
                }
            });
        } else if (incoming?.kind === 'SearchList') {
            listState = incoming;
        }
        if (!listState) {
            if (request.aggregateType === 'searchListLoadMore') {
                updateFilter(
                    filterKey,
                    { hasMoreItems: false, after: undefined },
                    false,
                    subFilterKey,
                );
            }
            if (request.aggregateType === 'searchListSearch') {
                lastSearchQueryRef.current = null;
            }
            return;
        }

        const returned = (listState as SearchListFilterState).items ?? [];
        const latest = stateRef.current;
        const merged =
            request.aggregateType === 'searchListLoadMore'
                ? [
                      ...latest.items,
                      ...returned.filter(
                          (item) =>
                              !latest.items.find((i) => i.value === item.value),
                      ),
                  ]
                : [...returned];
        if (!latest.rawSort) {
            merged.sort((a, b) => compareSearchListLabels(a.label, b.label));
        }
        updateFilter(
            filterKey,
            {
                items: merged,
                after: (listState as SearchListFilterState).after,
                hasMoreItems:
                    (listState as SearchListFilterState).hasMoreItems ??
                    returned.length >= LOAD_SIZE,
            },
            false,
            subFilterKey,
        );
    };

    const runSearch = (query: string) => {
        if (lastSearchQueryRef.current === query) return;

        lastSearchQueryRef.current = query;
        searchGenerationRef.current += 1;
        const generation = searchGenerationRef.current;
        requestInFlightRef.current = true;
        setAggregating(true);
        updateFilter(
            filterKey,
            {
                search: query,
                hasMoreItems: false,
                after: undefined,
            },
            false,
            subFilterKey,
        );
        const aggregationsParams: SearchAggregationParams = subFilterKey
            ? { aggFacetOptions: [{ facet: subFilterKey, search: query }] }
            : {
                  [SEARCH_LIST_AGG_PARAM_KEY_MAP[filterKey as SearchListKeys]]:
                      {
                          after: undefined,
                          size: LOAD_SIZE,
                          search: query,
                      },
              };
        aggregate({
            aggregateType: 'searchListSearch',
            aggregateFilter: filterKey as SearchListKeys,
            filterStates,
            aggregationsParams,
            onAggregateStateChange: (request, aggState, data) =>
                applyAggregatedItems(request, aggState, data, generation),
        });
    };
    const { run: debouncedSearch, cancel: cancelDebouncedSearch } =
        useDebounceFn(runSearch, { wait: 500 });

    useEffect(
        () => () => {
            cancelDebouncedSearch();
            searchGenerationRef.current += 1;
        },
        [cancelDebouncedSearch],
    );

    const loadMore = () => {
        // Facet lists (subFilterKey) return their full set in one aggregation.
        if (
            requestInFlightRef.current ||
            !hasMoreItems ||
            !after ||
            subFilterKey
        ) {
            return;
        }
        requestInFlightRef.current = true;
        setAggregating(true);
        const generation = searchGenerationRef.current;
        aggregate({
            aggregateType: 'searchListLoadMore',
            aggregateFilter: filterKey as SearchListKeys,
            filterStates,
            aggregationsParams: {
                [SEARCH_LIST_AGG_PARAM_KEY_MAP[filterKey as SearchListKeys]]: {
                    after,
                    size: LOAD_SIZE,
                    search,
                },
            },
            onAggregateStateChange: (request, aggState, data) =>
                applyAggregatedItems(request, aggState, data, generation),
        });
    };

    const toggle = (item: FilterSearchListItem, checked: boolean) => {
        const next = selectedItems ? [...selectedItems] : [];
        const at = next.findIndex((i) => i.value === item.value);
        if (checked && at === -1) next.push({ ...item, checked: true });
        if (!checked && at !== -1) next.splice(at, 1);
        updateFilter(
            filterKey,
            { kind: 'SearchList', items, selectedItems: next },
            true,
            subFilterKey,
        );
    };

    return (
        <div>
            {!props.hideSearch ? (
                <div className="flex flex-col items-center justify-center gap-4 md:justify-between">
                    <div className="w-full grow">
                        <RoccoInput
                            color="outline"
                            text="small"
                            shape="rounded"
                            placeholder="Search"
                            value={draftSearch}
                            showClearButton
                            postfix={<Search className="size-4.5" />}
                            style={{ fontSize: 16 }}
                            onFocus={() => {
                                searchFocusedRef.current = true;
                            }}
                            onBlur={() => {
                                searchFocusedRef.current = false;
                            }}
                            onChange={(event) => {
                                const query = event.target.value;
                                searchGenerationRef.current += 1;
                                requestInFlightRef.current = false;
                                setDraftSearch(query);
                                debouncedSearch(query);
                            }}
                        />
                    </div>
                </div>
            ) : null}

            <div
                className={cn(
                    props.hideSearch ? undefined : 'mt-6',
                    aggregating && 'opacity-50',
                )}
            >
                <div className="flex flex-col gap-2">
                    {items.map((item) => (
                        <CheckboxRow
                            key={item.value}
                            id={`${filterKey}-${subFilterKey ?? ''}-${item.value}`}
                            label={item.label}
                            disabled={loading}
                            checked={
                                !!selectedItems?.find(
                                    (s) => s.value === item.value,
                                )
                            }
                            onToggle={(checked) => toggle(item, checked)}
                        />
                    ))}
                    {items.length === 0 && search ? (
                        <p className="m-0 py-2 text-sm text-gray-500">
                            No result for “{search}”
                        </p>
                    ) : null}
                </div>

                {hasMoreItems && !subFilterKey && items.length > 0 ? (
                    <div
                        className="mt-10 flex cursor-pointer flex-col items-center"
                        onClick={loadMore}
                    >
                        <span className="font-medium text-gray-900">
                            Display more
                        </span>
                        <ChevronDown className="size-4.5" />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

/**
 * The Colour filter — a two-column grid of swatch dots + capitalised names,
 * matching the category Refine. The swatch comes from the facet label (a hex or
 * gradient) and the name from its value; toggling stages the same CheckboxGroup
 * payload as the plain group.
 */
const RefineColorGroup = ({
    filter,
    state,
    ctx,
}: {
    filter: CheckboxGroupFilter;
    state: CheckboxGroupFilterState;
    ctx: RefineControlContext;
}) => {
    const checked = state.checkedItems ?? [];
    return (
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            {(state.items ?? []).map((item) => {
                const isChecked = checked.some((c) => c.value === item.value);
                const background = getSafeSwatchBackground(item.label);
                const isLight =
                    typeof item.label === 'string' &&
                    ['#fff', '#ffffff'].includes(item.label.toLowerCase());
                return (
                    <button
                        key={item.value ?? item.label}
                        type="button"
                        disabled={ctx.loading || item.disabled}
                        onClick={() =>
                            ctx.updateFilter(filter.filterKey, {
                                kind: 'CheckboxGroup',
                                checkedItems: isChecked
                                    ? checked.filter(
                                          (c) => c.value !== item.value,
                                      )
                                    : [...checked, item],
                            })
                        }
                        className="flex items-center gap-3 text-left"
                    >
                        <span
                            className={cn(
                                'relative flex size-5 shrink-0 items-center justify-center rounded-full',
                                isLight ? 'border border-gray-300' : '',
                            )}
                            style={{ background }}
                        >
                            {isChecked ? (
                                <span className="flex size-3.5 items-center justify-center rounded-full bg-black/60">
                                    <Check className="size-2.5 text-white" />
                                </span>
                            ) : null}
                        </span>
                        <span className="font-sans text-sm text-gray-900 capitalize">
                            {item.value ?? item.label}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

/** True when every option's label is a colour value (hex or gradient). */
const isColourGroup = (state: CheckboxGroupFilterState): boolean =>
    !!state.items?.length &&
    state.items.every(
        (item) =>
            typeof item.label === 'string' &&
            (item.label.startsWith('#') ||
                item.label.startsWith('linear-gradient')),
    );

/** A plain checkbox group (all options visible), same row styling as the list. */
const RefineCheckboxGroup = ({
    filter,
    state,
    ctx,
}: {
    filter: CheckboxGroupFilter;
    state: CheckboxGroupFilterState;
    ctx: RefineControlContext;
}) => {
    const checked = state.checkedItems ?? [];
    return (
        <div className="flex flex-col gap-2">
            {(state.items ?? []).map((item) => {
                const isChecked = checked.some((c) => c.value === item.value);
                return (
                    <CheckboxRow
                        key={item.value ?? item.label}
                        id={`${filter.filterKey}-${item.value ?? item.label}`}
                        label={item.label}
                        disabled={ctx.loading}
                        checked={isChecked}
                        onToggle={(next) =>
                            ctx.updateFilter(filter.filterKey, {
                                kind: 'CheckboxGroup',
                                checkedItems: next
                                    ? [...checked, item]
                                    : checked.filter(
                                          (c) => c.value !== item.value,
                                      ),
                            })
                        }
                    />
                );
            })}
        </div>
    );
};

/**
 * A single-select group rendered as pills, matching the category Refine's
 * multi-value pill buttons. The underlying modules state is single-select, so
 * exactly one pill is active at a time.
 */
const RefineRadioGroup = ({
    filter,
    state,
    ctx,
}: {
    filter: RadioGroupFilter;
    state: RadioGroupFilterState;
    ctx: RefineControlContext;
}) => {
    const items = state.items ?? [];
    const selected = state.selectedItem ?? items[0];
    return (
        <div className="flex flex-wrap gap-2 pt-2">
            {items.map((item) => (
                <PillButton
                    key={item.value ?? item.label}
                    size="sm"
                    selected={item.value === selected?.value}
                    disabled={ctx.loading}
                    onClick={() =>
                        ctx.updateFilter(filter.filterKey, {
                            kind: 'RadioGroup',
                            selectedItem: item,
                        })
                    }
                >
                    {item.label}
                </PillButton>
            ))}
        </div>
    );
};

/** A lone boolean checkbox. */
const RefineCheckbox = ({
    filter,
    state,
    ctx,
}: {
    filter: CheckboxFilter;
    state: CheckboxFilterState;
    ctx: RefineControlContext;
}) => (
    <div className="flex flex-col gap-2">
        <CheckboxRow
            id={filter.filterKey}
            label={String(filter.props.label ?? filter.filterKey)}
            disabled={ctx.loading || state.disabled}
            checked={!!state.checked}
            onToggle={(checked) =>
                ctx.updateFilter(filter.filterKey, {
                    kind: 'Checkbox',
                    checked,
                })
            }
        />
    </div>
);

const clampToBounds = (value: number, min: number, max: number): number =>
    Math.max(min, Math.min(value, max));

const digitsOnly = (raw: string): string => raw.replace(/[^0-9]/g, '');

/**
 * A numeric range as two typed min/max inputs (Size dimensions, Price …),
 * matching the category's simple-range element rather than aria's slider. The
 * bounds come from the aggregated state; committing on blur clamps and
 * normalises, and clearing both fields removes the filter.
 */
const RefineRange = ({
    filter,
    state,
    ctx,
}: {
    filter: RangeSelectorFilter;
    state: RangeSelectorFilterState;
    ctx: RefineControlContext;
}) => {
    const { filterKey, props } = filter;
    const bounds = state.range ?? props.range ?? { min: 0, max: 0 };
    const value = state.value;
    const unit = typeof props.unit === 'string' ? props.unit : undefined;
    const unitAtStart = props.unitPosition === 'start';
    const title = typeof props.title === 'string' ? props.title : undefined;

    const [minText, setMinText] = useState('');
    const [maxText, setMaxText] = useState('');
    useEffect(() => {
        // Nullish (not truthy) — a committed 0 bound is legitimate for
        // price/size and must stay in the field rather than clear to blank.
        setMinText(value?.min != null ? String(value.min) : '');
        setMaxText(value?.max != null ? String(value.max) : '');
    }, [value?.min, value?.max]);

    // Nothing to filter on when the aggregated bounds are empty.
    if (bounds.min === 0 && bounds.max === 0) return null;

    const commit = () => {
        if (!minText && !maxText) {
            ctx.updateFilter(filterKey, {
                kind: 'RangeSelector',
                value: undefined,
            });
            return;
        }
        const rawMin = minText ? Number(minText) : bounds.min;
        const rawMax = maxText ? Number(maxText) : bounds.max;
        const min = clampToBounds(rawMin, bounds.min, bounds.max);
        const max = clampToBounds(rawMax, bounds.min, bounds.max);
        ctx.updateFilter(filterKey, {
            kind: 'RangeSelector',
            value: { min: Math.min(min, max), max: Math.max(min, max) },
        });
    };

    const unitEl = unit ? (
        <span className="text-xs text-gray-400">{unit}</span>
    ) : undefined;

    const field = (
        text: string,
        setText: (next: string) => void,
        placeholder: number,
    ) => (
        <RoccoInput
            type="text"
            inputMode="numeric"
            color="blackOutline"
            focus="bordered"
            placeholder={String(placeholder)}
            prefix={unitAtStart ? unitEl : undefined}
            postfix={unitAtStart ? undefined : unitEl}
            value={text}
            disabled={ctx.loading}
            onChange={(event) => setText(digitsOnly(event.target.value))}
            onBlur={commit}
            // Stated here: the bordered focus variant's `border-box` is read
            // as a border colour and drops `blackOutline`'s, which left these
            // fields with no outline and no fill — invisible on the sheet.
            className="border-[#141414]/20 bg-white"
        />
    );

    return (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            {title ? (
                <span className="min-w-16 font-sans text-sm font-medium text-gray-900">
                    {title}
                </span>
            ) : null}
            <div className="flex flex-1 items-center gap-1.5">
                {field(minText, setMinText, bounds.min)}
                <span className="text-gray-400">–</span>
                {field(maxText, setMaxText, bounds.max)}
            </div>
        </div>
    );
};

/** Flatten the legacy award tree into flat leaf options. */
const flattenAwardOptions = (nodes: AwardCheckboxTreeNode[]) =>
    nodes.flatMap((node) => (node.children?.length ? node.children : [node]));

/** Convert the legacy award tree nodes into the shared tree's group shape. */
const toAwardsTreeGroups = (
    nodes: AwardCheckboxTreeNode[],
): AwardsTreeGroup[] =>
    nodes.map((node) => ({
        label: node.label ?? String(node.value ?? ''),
        value: String(node.value ?? ''),
        leaves: (node.children?.length ? node.children : [node]).map(
            (leaf) => ({
                label: leaf.label ?? String(leaf.value ?? ''),
                value: String(leaf.value ?? ''),
            }),
        ),
    }));

/**
 * The awards filter as the shared accessible checkbox tree, staged through the
 * sheet's `updateFilter` like every other refine control.
 */
const RefineAwardsTree = ({
    filter,
    state,
    ctx,
}: {
    filter: AwardsFilter;
    state: AwardFilterState;
    ctx: RefineControlContext;
}) => {
    const groups = useMemo(
        () => toAwardsTreeGroups(state.items),
        [state.items],
    );
    const leafValues = useMemo(
        () => groups.flatMap((group) => group.leaves.map((leaf) => leaf.value)),
        [groups],
    );
    const checkedValues = useMemo(() => {
        const values = (state.checkedItems ?? []).map((item) =>
            String(item.value ?? ''),
        );
        return new Set(values.includes('all') ? leafValues : values);
    }, [state.checkedItems, leafValues]);

    const handleToggle = (values: string[], checked: boolean) => {
        const next = applyAwardsSelection(
            checkedValues,
            leafValues,
            values,
            checked,
        );
        ctx.updateFilter(filter.filterKey, {
            kind: 'AwardsFilter',
            checkedItems: flattenAwardOptions(state.items).filter((option) =>
                next.has(String(option.value ?? '')),
            ),
        });
    };

    return (
        <AwardsCheckboxTree
            groups={groups}
            checkedValues={checkedValues}
            onToggle={handleToggle}
        />
    );
};

/**
 * Option text that more than one suggestion shares, such as the Nevada county
 * and city that both read "Carson City, Nevada". Only those options show their
 * type label.
 */
const getRepeatedHeaders = (options: { header: string }[]): Set<string> => {
    const seen = new Set<string>();
    const repeated = new Set<string>();
    for (const { header } of options) {
        if (seen.has(header)) repeated.add(header);
        seen.add(header);
    }
    return repeated;
};

/**
 * The location filter with rocco primitives: a search input over the place
 * finder's suggestions plus the "Use my current location" action, writing the
 * staged modules `Location` state.
 */
const RefineLocation = ({
    filter,
    state,
    ctx,
}: {
    filter: LocationFilter;
    state: LocationFilterState;
    ctx: RefineControlContext;
}) => {
    const { code: countryCode } = useCountryContext();
    const selectedText = state.selectedLocation
        ? formatLocationSearchFilterText(state.selectedLocation)
        : '';
    const [query, setQuery] = useState(selectedText);
    const [editing, setEditing] = useState(false);
    const awaitingCurrentLocation = useRef(false);

    useEffect(() => {
        setQuery(selectedText);
    }, [selectedText]);

    const {
        changeKeyword,
        searchFilterOptions,
        optionsKeyword,
        loading: placesLoading,
    } = usePlaceFinder({
        type: FindPlaceTypeEnum.SearchFilter,
        limit: 10,
        apiProvider: FindPlaceApiProviderEnum.ServiceableArea,
    });

    const applyLocation = (place?: BasicPlace) => {
        ctx.updateFilter(filter.filterKey, {
            kind: 'Location',
            selectedLocation: place,
        });
    };

    useEffect(() => {
        if (ctx.loading) return;
        if (ctx.currentLocation && awaitingCurrentLocation.current) {
            setQuery(formatLocationSearchFilterText(ctx.currentLocation));
            setEditing(false);
            applyLocation(ctx.currentLocation);
        }
        awaitingCurrentLocation.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx.currentLocation, ctx.loading]);

    const trimmed = query.trim();
    const suggestionsFresh =
        editing &&
        trimmed.length >= MIN_PLACE_FINDER_KEYWORD_LENGTH &&
        trimmed === optionsKeyword &&
        !placesLoading;

    const suggestionsOpen = suggestionsFresh && searchFilterOptions.length > 0;
    const repeatedHeaders = useMemo(
        () => getRepeatedHeaders(searchFilterOptions),
        [searchFilterOptions],
    );

    return (
        <div
            className="flex flex-col gap-2"
            // Close the list when focus leaves the control entirely; option
            // clicks keep focus inside, so selection still lands first.
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    setEditing(false);
                }
            }}
            onKeyDown={(event) => {
                if (event.key === 'Escape') setEditing(false);
            }}
        >
            <RoccoInput
                color="gray"
                text="small"
                value={query}
                placeholder={getLocationFilterPlaceholder(countryCode)}
                aria-label="Search by location"
                role="combobox"
                aria-expanded={suggestionsOpen}
                aria-haspopup="listbox"
                showClearButton
                postfix={<Search className="size-4.5" />}
                onChange={(event) => {
                    const next = event.target.value;
                    setQuery(next);
                    setEditing(true);
                    changeKeyword(next);
                }}
                onFocus={() => setEditing(true)}
                onClear={() => {
                    setQuery('');
                    setEditing(false);
                    applyLocation(undefined);
                }}
            />
            {suggestionsOpen && (
                <div className="flex flex-col" role="listbox">
                    {searchFilterOptions.map((option) => (
                        <button
                            key={option.value.serviceableAreaID}
                            type="button"
                            role="option"
                            aria-selected={false}
                            className="px-1 py-1.5 text-left text-sm hover:bg-gray-100"
                            onClick={() => {
                                setQuery(option.header);
                                setEditing(false);
                                applyLocation(option.value);
                            }}
                        >
                            {option.header}
                            {repeatedHeaders.has(option.header) && (
                                <>
                                    <span className="sr-only">, </span>
                                    <span className="block text-xs text-gray-500">
                                        {option.placeType}
                                    </span>
                                </>
                            )}
                        </button>
                    ))}
                </div>
            )}
            <button
                type="button"
                disabled={ctx.currentLocationDisabled}
                className="w-fit text-sm text-[#343434] underline underline-offset-2 disabled:opacity-50"
                onClick={() => {
                    awaitingCurrentLocation.current = true;
                    ctx.getCurrentLocation();
                }}
            >
                Use my current location
            </button>
        </div>
    );
};

/**
 * Renders a filter control with the category Refine's rocco primitives
 * (SearchList / CheckboxGroup / RadioGroup / Checkbox / Location / Awards).
 */
export const renderRefineControl = (
    filter: FilterType,
    state: FilterState,
    ctx: RefineControlContext,
): JSX.Element | null => {
    switch (filter.kind) {
        case 'SearchList':
            return (
                <RefineSearchableList
                    filter={filter}
                    state={state as SearchListFilterState}
                    ctx={ctx}
                />
            );
        case 'CheckboxGroup': {
            const groupState = state as CheckboxGroupFilterState;
            if (!groupState.items?.length) return null;
            if (isColourGroup(groupState)) {
                return (
                    <RefineColorGroup
                        filter={filter}
                        state={groupState}
                        ctx={ctx}
                    />
                );
            }
            return (
                <RefineCheckboxGroup
                    filter={filter}
                    state={groupState}
                    ctx={ctx}
                />
            );
        }
        case 'RadioGroup': {
            const radioState = state as RadioGroupFilterState;
            if (!radioState.items?.length) return null;
            return (
                <RefineRadioGroup
                    filter={filter}
                    state={radioState}
                    ctx={ctx}
                />
            );
        }
        case 'Checkbox': {
            const checkboxState = state as CheckboxFilterState;
            // hideInFilters hides the box in the sheet (it still shows as an
            // applied chip); adminOnly is marketing-view only.
            if (
                checkboxState.disabled ||
                filter.hideInFilters ||
                (filter.adminOnly && !ctx.marketingView)
            )
                return null;
            return (
                <RefineCheckbox
                    filter={filter}
                    state={checkboxState}
                    ctx={ctx}
                />
            );
        }
        case 'RangeSelector':
            return (
                <RefineRange
                    filter={filter}
                    state={state as RangeSelectorFilterState}
                    ctx={ctx}
                />
            );
        case 'Location':
            return (
                <RefineLocation
                    filter={filter}
                    state={state as LocationFilterState}
                    ctx={ctx}
                />
            );
        case 'AwardsFilter':
            return (
                <RefineAwardsTree
                    filter={filter}
                    state={state as AwardFilterState}
                    ctx={ctx}
                />
            );
        default:
            return null;
    }
};
